import { useEffect, useLayoutEffect, useState, type CSSProperties, type ReactNode } from 'react';
import { oversightEventStream, oversightEvents, type ApiClient, type ApiError, type OversightEvent, type OversightResident, type Role } from '@aithora/shared';
import { useSession } from '../../session/SessionContext';
import { useQuery, type QueryState } from '../../useQuery';
import { sx } from '../../ui/designStyles';
import { tierLabel } from '../residents/common';

/** Load one route with the session's client (re-runs when `deps` change). */
export function useApi<T>(load: (client: ApiClient, signal: AbortSignal) => Promise<T>, deps: unknown[] = [], opts: { enabled?: boolean; pollMs?: number } = {}): QueryState<T> {
  const { client, session } = useSession();
  return useQuery<T>((signal) => load(client, signal), [client, session?.token, ...deps], opts);
}

/**
 * A design `<a href="#">` that acts (never navigates): a button with no chrome, the
 * design's inline style on top.
 */
export function Act({
  css,
  onClick,
  children,
  disabled,
  title,
  label,
  pressed,
  extra,
}: {
  css: string;
  onClick?: () => void;
  children: ReactNode;
  disabled?: boolean;
  title?: string;
  label?: string;
  pressed?: boolean;
  extra?: CSSProperties;
}) {
  return (
    <button
      type="button"
      className="dlink"
      style={extra ? { ...sx(css), ...extra } : sx(css)}
      onClick={onClick}
      disabled={disabled}
      aria-disabled={disabled || undefined}
      title={title}
      aria-label={label}
      aria-pressed={pressed}
    >
      {children}
    </button>
  );
}

// ── Who may do what (the server enforces the same rules) ─────────────────────

/** A stored line in plain words: record ids (uuids) are left out, so a row never shows a
 *  resident's or an entry's id (QA-10). The expanded detail still carries the whole record. */
export function plainWords(text: string | null | undefined): string {
  return String(text ?? '')
    .replace(/[\s:(#]*\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b\)?/gi, '')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

export const isFacilityAdmin = (role: Role | null) => role === 'ADMIN';
/** Aithora's operator tier: every building, and the only ones who may press KILL ALL. */
export const isOperatorTier = (role: Role | null) => role === 'SUPER_ADMIN' || role === 'OPERATOR';
/**
 * Who acts on a building's held items and alerts: its own Facility Admin. V9 (change log Oct
 * 28, round 4): Oversight action buttons are hidden for every Aithora role, and Super Admin
 * reads facility work without acting on it; the Owner/Operator only reads.
 */
export const answersHeld = (role: Role | null) => role === 'ADMIN';

/** The line a read-only seat sees where the actions would be ("Read-only as Super Admin · …"). */
export function ReadOnlyAs({ role }: { role: Role | null }) {
  return <span data-testid="ov-readonly" style={sx('font-size:14px;color:#54606C;')}>{'Read-only as ' + tierLabel(role) + ' · the facility admin decides'}</span>;
}

/** The platform scope id the ledger routes take for "every building" (ledger-ops scopeExists). */
export const PLATFORM_SCOPE = '00000000-0000-4000-8000-000000000000';

// ── Clocks and durations ─────────────────────────────────────────────────────

/** Re-render every `ms` so clocks and "waiting" ages move. */
export function useNow(ms = 1000): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), ms);
    return () => window.clearInterval(id);
  }, [ms]);
  return now;
}

const p2 = (n: number) => ('0' + n).slice(-2);
/** 19:12:00 (UTC). */
export const utcClock = (d: Date) => p2(d.getUTCHours()) + ':' + p2(d.getUTCMinutes()) + ':' + p2(d.getUTCSeconds());
/** 14:12:00 in the viewer's zone (design toTimeString().slice(0, 8)). */
export const localClock = (d: Date) => d.toTimeString().slice(0, 8);
/** "4m 17s": minutes and zero-padded seconds (design age()). */
export function ageText(sec: number): string {
  const s = Math.max(0, Math.floor(sec));
  return Math.floor(s / 60) + 'm ' + p2(s % 60) + 's';
}
/** A median time: "6m 40s", "45s", "1h 5m". */
export function durText(sec: number | null | undefined): string {
  if (sec === null || sec === undefined || !Number.isFinite(sec)) return '–';
  const s = Math.round(sec);
  if (s < 60) return s + 's';
  if (s < 3600) return Math.floor(s / 60) + 'm ' + p2(s % 60) + 's';
  return Math.floor(s / 3600) + 'h ' + Math.floor((s % 3600) / 60) + 'm';
}

// ── Names ────────────────────────────────────────────────────────────────────

/** "Walter B", or the initials when the server does not send a short name. */
export function residentName(r: OversightResident | null | undefined): string | null {
  if (!r) return null;
  return r.display || r.initials || null;
}
/** "Walter B · Rm 112" (design who). */
export function residentLine(r: OversightResident | null | undefined): string | null {
  const n = residentName(r);
  if (!n) return null;
  return r?.room ? n + ' · Rm ' + r.room : n;
}

/** The design's verdict word for an event: the gate's verdict, or what kind of record it is. */
export function verdictTag(e: Pick<OversightEvent, 'verdict' | 'type'>): string {
  const v = (e.verdict || '').toUpperCase();
  if (v === 'CLEAR' || v === 'APPROVED' || v === 'ALLOWED' || v === 'PASS') return 'ALLOW';
  if (v === 'HELD') return 'HOLD';
  if (v === 'BLOCKED' || v === 'DENIED' || v === 'REFUSED') return 'BLOCK';
  if (v === 'ESCALATE' || v === 'ESCALATED') return 'ALERT';
  if (v) return v;
  const byType: Record<string, string> = { sensor: 'SENSOR', reminder: 'REMINDER', care_log: 'TASK', companion: 'CHECK-IN', escalation: 'ALERT', ledger: 'LEDGER', decision: 'LOG' };
  return byType[e.type] ?? 'LOG';
}

/** "L3 Allowlist": the guard's layer and name. */
export const guardLine = (e: { level: string | null; guard: string | null }) => [e.level, e.guard].filter(Boolean).join(' ');

// ── The design's layout switches ─────────────────────────────────────────────

/**
 * The design's width and height rules for the Summary (monV2): `tight` under 1450px of
 * console, `short` when the content pane is under 760px tall or an emergency line is up,
 * `shortL` under 960px. Measured from the shell, not the window.
 */
export function useShellFit(): { rw: number; tight: boolean; short: boolean; shortL: boolean } {
  const [fit, setFit] = useState(() => ({ rw: typeof window !== 'undefined' ? window.innerWidth : 1400, ch: 1000, alert: false }));
  useLayoutEffect(() => {
    const measure = () => {
      const root = document.querySelector('.shell') as HTMLElement | null;
      const cc = document.getElementById('console-content');
      const rw = root?.clientWidth || window.innerWidth || 1400;
      const ch = cc?.clientHeight || 1000;
      const alert = !!cc?.querySelector('.embanner');
      setFit((f) => (f.rw === rw && f.ch === ch && f.alert === alert ? f : { rw, ch, alert }));
    };
    measure();
    const cc = document.getElementById('console-content');
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    if (ro && cc) ro.observe(cc);
    const mo = typeof MutationObserver !== 'undefined' && cc ? new MutationObserver(measure) : null;
    if (mo && cc) mo.observe(cc, { childList: true });
    window.addEventListener('resize', measure);
    return () => {
      ro?.disconnect();
      mo?.disconnect();
      window.removeEventListener('resize', measure);
    };
  }, []);
  const short = fit.ch < 760 || fit.alert;
  return { rw: fit.rw, tight: fit.rw < 1450, short, shortL: short || fit.ch < 960 };
}

// ── Files ────────────────────────────────────────────────────────────────────

/** Hand the viewer a JSON file. */
export function downloadJson(name: string, obj: unknown): void {
  const blob = new Blob([JSON.stringify(obj, null, 2)], { type: 'application/json' });
  const u = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = u;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(u), 1500);
}

/** The canonical JSON the ledger export hashes (sorted keys), as ledger-ops canonicalJson. */
export function canonicalJson(v: unknown): string {
  if (v === null || typeof v !== 'object') return JSON.stringify(v) ?? 'null';
  if (Array.isArray(v)) return '[' + v.map((x) => canonicalJson(x)).join(',') + ']';
  const o = v as Record<string, unknown>;
  return '{' + Object.keys(o).filter((k) => o[k] !== undefined).sort().map((k) => JSON.stringify(k) + ':' + canonicalJson(o[k])).join(',') + '}';
}

export async function sha256Hex(text: string): Promise<string> {
  const buf = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, '0')).join('');
}

// ── The live feed ────────────────────────────────────────────────────────────

/**
 * The unified event feed, live: a backfill of the last `windowMs` from GET
 * /oversight/events, then GET /oversight/events/stream from the newest event on, read
 * with the session's auth. Reconnects after the retry the server asks for (or 3 s) from
 * the newest event it holds, so nothing is missed or doubled. Oldest first, at most
 * `limit` events.
 */
export function useEventFeed({ windowMs, limit, enabled = true }: { windowMs: number; limit: number; enabled?: boolean }): { events: OversightEvent[] | null; error: ApiError | null; streaming: boolean } {
  const { client, session } = useSession();
  const [events, setEvents] = useState<OversightEvent[] | null>(null);
  const [error, setError] = useState<ApiError | null>(null);
  const [streaming, setStreaming] = useState(false);
  useEffect(() => {
    if (!enabled) return;
    const ctrl = new AbortController();
    let held: OversightEvent[] = [];
    let timer = 0;
    const put = (list: OversightEvent[]) => {
      const byId = new Map(held.map((e) => [e.id, e]));
      for (const e of list) byId.set(e.id, e);
      held = [...byId.values()].sort((a, b) => Date.parse(a.at) - Date.parse(b.at)).slice(-limit);
      setEvents(held);
    };
    const since = () => (held.length ? Date.parse(held[held.length - 1].at) : Date.now());
    const open = async () => {
      if (ctrl.signal.aborted) return;
      let wait = 3000;
      try {
        setStreaming(true);
        const r = await oversightEventStream(client, { since: since(), limit, signal: ctrl.signal, onEvent: (e) => put([e]) });
        if (r.retryMs !== null) wait = r.retryMs;
      } catch (e) {
        if (ctrl.signal.aborted) return;
        wait = 5000;
        setError(e as ApiError);
      } finally {
        if (!ctrl.signal.aborted) setStreaming(false);
      }
      if (!ctrl.signal.aborted && wait < 3_600_000) timer = window.setTimeout(open, wait);
    };
    oversightEvents(client, { query: { from: new Date(Date.now() - windowMs).toISOString(), limit }, signal: ctrl.signal })
      .then((r) => {
        put(r.events);
        setError(null);
        void open();
      })
      .catch((e: unknown) => {
        if (!ctrl.signal.aborted) setError(e as ApiError);
      });
    return () => {
      ctrl.abort();
      window.clearTimeout(timer);
    };
  }, [client, session?.token, windowMs, limit, enabled]);
  return { events, error, streaming };
}
